import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, FileText, Upload, Trash2, Loader2, AlertCircle, CheckCircle, File, RefreshCw, Info, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { extrajudicialAPI } from '../api';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const TIPOS_PLANTILLA = [
  { id: 'hoja_encargo', nombre: 'Hoja de Encargo', descripcion: 'Documento de encargo profesional del cliente', icon: FileText, color: 'bg-green-100 text-green-700' },
  { id: 'reclamacion', nombre: 'Reclamación Extrajudicial', descripcion: 'Documento de reclamación formal a la entidad bancaria', icon: FileText, color: 'bg-blue-100 text-blue-700' }
];

const ConfiguracionPlantillasExtrajudicial = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plantillas, setPlantillas] = useState([]);
  const [uploading, setUploading] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plantilla: null });
  const fileInputRefs = useRef({});

  useEffect(() => { loadPlantillas(); }, []);

  const loadPlantillas = async () => {
    setLoading(true);
    try { const r = await extrajudicialAPI.getPlantillas(); setPlantillas(r.data || []); }
    catch (e) { toast({ title: "Error", description: "No se pudieron cargar", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const handleFileSelect = async (tipo, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(docx?|odt)$/i)) { toast({ title: "Tipo no válido", variant: "destructive" }); return; }
    setUploading(p => ({ ...p, [tipo]: true }));
    try {
      const fd = new FormData(); fd.append('archivo', file); fd.append('tipo', tipo); fd.append('nombre', file.name);
      await extrajudicialAPI.uploadPlantilla(fd);
      toast({ title: "Plantilla subida" }); await loadPlantillas();
    } catch (err) { toast({ title: "Error", variant: "destructive" }); }
    finally { setUploading(p => ({ ...p, [tipo]: false })); if (fileInputRefs.current[tipo]) fileInputRefs.current[tipo].value = ''; }
  };

  const handleDelete = async () => {
    if (!deleteDialog.plantilla) return;
    try { await extrajudicialAPI.deletePlantilla(deleteDialog.plantilla.id); toast({ title: "Eliminada" }); await loadPlantillas(); }
    catch (e) { toast({ title: "Error", variant: "destructive" }); }
    finally { setDeleteDialog({ open: false, plantilla: null }); }
  };

  const handleDownload = async (pl) => {
    try {
      const r = await extrajudicialAPI.downloadPlantilla(pl.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = pl.nombre_archivo || 'plantilla.docx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: "Error", description: "No se pudo descargar la plantilla", variant: "destructive" }); }
  };

  const getPlantillaByTipo = (t) => plantillas.find(p => p.tipo === t);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES') : '';

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/configuracion')}><ChevronLeft className="w-4 h-4 mr-2" />Volver</Button>
        <div className="flex-1"><h1 className="text-2xl font-bold">Plantillas Extrajudiciales</h1></div>
        <Button variant="outline" onClick={loadPlantillas}><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
      </div>
      <AvisoFinalizacionConfigV114 />
      <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4 flex gap-3"><Info className="w-5 h-5 text-blue-600" />
        <p className="text-sm text-blue-800">Sube plantillas Word con marcadores: <code className="bg-blue-100 px-1 rounded">{'{{cliente_nombre}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{banco_nombre}}'}</code></p>
      </CardContent></Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {TIPOS_PLANTILLA.map((tipo) => {
          const pl = getPlantillaByTipo(tipo.id), up = uploading[tipo.id], Icon = tipo.icon;
          return (<Card key={tipo.id} className={pl ? 'border-green-200 bg-green-50/30' : 'border-dashed border-2'}>
            <CardHeader className="pb-3"><div className="flex items-center gap-3"><div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tipo.color}`}><Icon className="w-5 h-5" /></div>
              <div><CardTitle className="text-base">{tipo.nombre}</CardTitle><CardDescription className="text-xs">{tipo.descripcion}</CardDescription></div></div></CardHeader>
            <CardContent>
              {pl ? (<div className="space-y-3"><div className="p-3 bg-white rounded border flex items-center gap-2"><File className="w-4 h-4 text-blue-600" /><span className="text-sm truncate flex-1">{pl.nombre_archivo}</span><span className="text-xs text-gray-500">{formatDate(pl.updated_at)}</span></div>
                <div className="flex items-center gap-2 text-green-700 text-sm"><CheckCircle className="w-4 h-4" />Configurada</div>
                <div className="flex gap-2"><Button variant="outline" size="sm" className="flex-1" onClick={() => fileInputRefs.current[tipo.id]?.click()} disabled={up}>{up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Reemplazar</Button>
                  <Button variant="outline" size="sm" data-testid={`descargar-plantilla-${tipo.id}`} onClick={() => handleDownload(pl)}><Download className="w-4 h-4" /></Button><Button variant="outline" size="sm" className="text-red-600" onClick={() => setDeleteDialog({ open: true, plantilla: pl })}><Trash2 className="w-4 h-4" /></Button></div></div>
              ) : (<div className="space-y-3"><div className="flex items-center gap-2 text-amber-600 text-sm"><AlertCircle className="w-4 h-4" />Sin plantilla</div>
                <Button variant="outline" className="w-full" onClick={() => fileInputRefs.current[tipo.id]?.click()} disabled={up}>{up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Subir plantilla</Button></div>)}
              <input type="file" ref={el => fileInputRefs.current[tipo.id] = el} className="hidden" accept=".doc,.docx,.odt" onChange={e => handleFileSelect(tipo.id, e)} />
            </CardContent></Card>);
        })}
      </div>

      <Card><CardHeader><CardTitle className="text-base">Marcadores disponibles</CardTitle>
        <CardDescription>Utiliza estos marcadores en tus plantillas Word</CardDescription></CardHeader>
        <CardContent><div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="font-semibold text-gray-700 mb-2">Cliente</p>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">XXCLIENTEXX</code> - Nombre</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">CLIENTXXX</code> - Nombre</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">XXDNIXX</code> - DNI</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">DNIXXX</code> - DNI</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">DIRECCION</code> - Dirección</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">TELEFONO</code> - Teléfono</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">EMAIL</code> - Email</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">ADREÇAXXX</code> - Dirección</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="font-semibold text-gray-700 mb-2">Banco</p>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">XXBANCOXX</code> - Banco actual</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">BANCXXX</code> - Banco actual</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">NOMBREEXBANCO</code> - Banco anterior</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="font-semibold text-gray-700 mb-2">Notario e Hipoteca</p>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">XXNOTARIOXX</code> - Notario</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">FECHAHIPOTECA</code> - Fecha hipoteca</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">PROTOCOLO</code> - Nº protocolo</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">LOCALIDAD</code> - Localidad</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="font-semibold text-gray-700 mb-2">Fechas, Importes y Referencia</p>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">NUMEROREFERENCIA</code> - Nº expediente</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">NUMEROEXPEDIENTEPRINCIPAL</code> - Nº expediente principal (judicial)</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">FECHAACTUAL</code> - Fecha dd/mm/aaaa</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">FECHA</code> - Fecha formato largo</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">TOTALPRINCIPAL</code> - Importe principal</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">TOTALINTERESES</code> - Intereses</div>
            <div><code className="text-xs text-blue-600 bg-blue-50 px-1">TOTALRECLAMACION</code> - Total</div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg space-y-1 md:col-span-2">
            <p className="font-semibold text-green-700 mb-2">Cláusulas Reclamadas</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_GASTOS</code></div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_COMISION_APERTURA</code></div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_POSICIONES_DEUDORAS</code></div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_SUELO</code></div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_REDONDEO</code></div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_INTERES_DEMORA</code></div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_CIERRE_IRPH</code></div>
            </div>
            <p className="text-xs text-green-600 mt-1">Devuelven "Sí" o "No"</p>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_PERSONALIZADA</code> - Texto</div>
              <div><code className="text-xs text-green-600 bg-green-100 px-1">CLAUSULA_PERSONALIZADA_NOMBRE</code> - Nombre</div>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg space-y-1 md:col-span-2">
            <p className="font-semibold text-purple-700 mb-2">Argumentos IA (texto generado por el Asistente)</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOCOMISIONAPERTURA</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOGASTOS</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOPOSICIONESDEUDORAS</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOSUELO</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOREDONDEO</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOINTERESDEMORA</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOIRPH</code></div>
              <div><code className="text-xs text-purple-600 bg-purple-100 px-1">ARGUMENTOANATOCISMO</code></div>
            </div>
            <p className="text-xs text-purple-600 mt-1">Se rellenan con el texto que generes/guardes en el Asistente de Argumentos IA (pestaña Cláusulas de la reclamación).</p>
          </div>
        </div></CardContent></Card>
      <AlertDialog open={deleteDialog.open} onOpenChange={o => setDeleteDialog({ open: o, plantilla: null })}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle><AlertDialogDescription>Se eliminará permanentemente.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600">Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>);
};

const AvisoFinalizacionConfigV114 = () => {
  const API_V114 = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [emails, setEmails] = React.useState('');
  const [enabled, setEnabled] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const authH = () => {
    const t = obtenerToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  React.useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_V114}/extrajudicial/config/notificacion`, { headers: authH() });
        setEmails(r.data?.emails || '');
        setEnabled(r.data?.enabled !== false);
      } catch (e) { /* noop */ } finally { setLoading(false); }
    })();
  }, []);
  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_V114}/extrajudicial/config/notificacion`, { emails, enabled }, { headers: authH() });
      toast({ title: 'Configuración guardada', description: 'Aviso de finalización actualizado.' });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <Card data-testid="aviso-finalizacion-config">
      <CardHeader>
        <CardTitle className="text-base">Aviso de finalización (email)</CardTitle>
        <CardDescription>
          Al crear el expediente judicial desde una reclamación extrajudicial, el sistema
          enviará un email de aviso (desde info@lexduo.cat) con el resumen del caso a estas direcciones.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="aviso-enabled-checkbox"
            className="w-4 h-4"
          />
          Enviar aviso de finalización
        </label>
        <div>
          <p className="text-sm font-medium mb-1">Destinatarios (separados por comas)</p>
          <textarea
            rows={2}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="abogado@despacho.com, gestion@despacho.com"
            data-testid="aviso-emails-input"
            className="w-full border rounded-md p-2 text-sm"
          />
        </div>
        <Button onClick={guardar} disabled={saving || loading} data-testid="aviso-guardar-btn">
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ConfiguracionPlantillasExtrajudicial;
